import { AuthError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabase';

/**
 * Email + password sign-in.
 *
 * Not the primary path for either brand — Google and OTP are (docs/ARCHITECTURE.md
 * §3). This exists because those two need a working consent screen and a
 * deliverable inbox respectively, so neither can be exercised while the rest of
 * the product is being built. A password account can be created straight in the
 * database and used the same second.
 *
 * Unlike `signInWithGoogle`, this resolves in place: there is no redirect, so the
 * caller stays mounted and is responsible for navigating afterwards.
 */

/** Why a sign-in attempt failed, already collapsed to what the UI should say. */
export type PasswordSignInFailure = 'invalidCredentials' | 'unconfirmedEmail' | 'unknown';

export interface PasswordSignInResult {
  readonly ok: boolean;
  readonly failure?: PasswordSignInFailure;
}

/**
 * Supabase distinguishes "no such user" from "wrong password" internally but
 * deliberately returns the same error for both, and so do we: telling an
 * attacker which emails exist is a free user-enumeration oracle.
 */
function classify(error: AuthError): PasswordSignInFailure {
  const code = error.code ?? '';
  if (code === 'email_not_confirmed') return 'unconfirmedEmail';
  if (code === 'invalid_credentials') return 'invalidCredentials';
  // Older Supabase versions report the same condition by message only.
  if (error.message.toLowerCase().includes('invalid login credentials')) {
    return 'invalidCredentials';
  }
  return 'unknown';
}

export async function signInWithPassword(
  email: string,
  password: string,
): Promise<PasswordSignInResult> {
  const { error } = await supabase.auth.signInWithPassword({
    email: email.trim(),
    password,
  });

  if (!error) return { ok: true };
  if (error instanceof AuthError) return { ok: false, failure: classify(error) };
  return { ok: false, failure: 'unknown' };
}

/**
 * Ends the session on this domain only. The other brand's session lives under a
 * different storage key and is intentionally untouched.
 */
export async function signOut(): Promise<void> {
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}
